import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterOutlet } from '@angular/router';
import { Extension, ExtensionScope, initExtension } from '@flywheel-io/extension';
import { FwButtonModule, FwIconModule, FwProgressModule } from '@flywheel-io/vision';
import * as nifti from 'nifti-reader-js';

import { initMock } from './mock-extension';

interface Point {
  x: string;
  y: string;
  z: string;
  vertex: number;
  value?: number;
}

interface Volume {
  data: number[];
  shapeX: number;
  shapeY: number;
  shapeZ: number;
}

declare var BrainBrowser: any;

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    RouterOutlet,
    FwButtonModule,
    FwIconModule,
    FwProgressModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  extension: Extension | null = null;
  viewer: any;
  isMni = false;
  bbrowserColor: string = '';
  nifti = nifti;
  data: number[] = [];
  hasMniTemplate = false;
  ticketUrl = '';
  loadingBB = true;
  lastError: string | null = null;
  showSlider = false;
  selectedShapeName = '';
  opacity = 100;
  selectedPoint: Point = {
    x: '',
    y: '',
    z: '',
    vertex: 0,
  }
  niftiHeader: nifti.NIFTI1 | nifti.NIFTI2 | null = null;
  niftiImage: ArrayBuffer | null = null;

  private runningLocally = false;
  

  constructor(private http: HttpClient) {
    BrainBrowser.config.set('worker_dir', 'workers');
    BrainBrowser.events.addEventListener('error', (ev: {message: string}) => {
      this.lastError = ev.message;
    });

    this.runningLocally = window.top === window.self;
    const init = this.runningLocally ? initMock : initExtension;
    init({ scope: ExtensionScope.ReadOnly, validateOrigin: () => true })
      .then((extension) => {
        this.extension = extension;
        this.isMni = (extension?.file?.name || '').indexOf('mni.obj') > 0;
        return extension.getFileUrl(extension.file);
      })
      .then((ticketUrl) => {
        this.ticketUrl = ticketUrl;
        this.initBrainBrowser();
      });

    window.addEventListener('resize', () => {
      if (this.viewer) {
        this.viewer.render();
      }
    });
  }

  get xRotActive(): boolean {
    return this.viewer?.autorotate?.x || false;
  }

  get yRotActive(): boolean {
    return this.viewer?.autorotate?.y || false;
  }

  get zRotActive(): boolean {
    return this.viewer?.autorotate?.z || false;
  }

  initBrainBrowser(): void {
    const scope = this;
    BrainBrowser.SurfaceViewer.start('bbrowser-viewer', function (viewer: any) {
      var elemViewer = document.querySelector('#bbrowser-viewer') as HTMLElement;
      scope.viewer = viewer;
      viewer.render();
      if (scope.runningLocally) {
        scope.ticketUrl = 'workers/brain.obj';
      }
      if (scope.isMni) {
        // FIXME: Code below was found in the original code, but the dummy.obj file does not appear to load into the viewer.
        // I found the dummy.obj searching git history for another file, perhaps there is a time earlier when it was compatible?
        // `git log --all --full-history -- "**/mni_152_template_t1.nii"`, I went back to 8375dedeeaaac21e47b44a2d9e23dbe0fcceea07, April 2019
        /*
        elemViewer.style.display = 'none';
        // this obj is a hack to make brainbrowser work
        viewer.loadModelFromURL('workers/dummy.obj', {
          format: 'mniobj',
          complete: function () {
            elemViewer.style.display = 'block';
            viewer.clearScreen();
            viewer.render();
            scope.bbrowserColor = '#808080';
            scope.setBackgroundcolor('#FFFFFF');
            viewer.loadModelFromURL(scope.ticketUrl, {
              format: 'mniobj',
              complete: function () {
                scope.setBackgroundcolor(scope.bbrowserColor);
                scope.loadingBB = false;
              },
            });
          },
        });
        */
      } else {
        scope.bbrowserColor = '#808080';
        scope.setBackgroundcolor(scope.bbrowserColor);
        scope.setBackgroundcolor('#FFFFFF');
        viewer.loadModelFromURL(scope.ticketUrl, {
          format: 'wavefrontobj',
          complete: function () {
            scope.setBackgroundcolor(scope.bbrowserColor);
            scope.loadingBB = false;
          },
        });
        viewer.addEventListener('displaymodel', function (ev: any) {
          if (ev.model.children.length) {
            scope.selectedShapeName = ev.model.children[0].name;
            scope.showSlider = true;
          }
        });
        elemViewer.addEventListener('click',
          function (event) {
            if (!event.shiftKey) {
              return;
            }
            var pick_info = viewer.pick(event.offsetX, event.offsetY);

            if (pick_info) {
              var selectedPoint = scope.selectedPoint;
              selectedPoint.x = pick_info.point.x.toPrecision(4);
              selectedPoint.y = pick_info.point.y.toPrecision(4);
              selectedPoint.z = pick_info.point.z.toPrecision(4);
              selectedPoint.vertex = pick_info.index;
              var picked_object = pick_info.object;
              var model_data = viewer.model_data.get(picked_object.userData.model_name);
              var intensity_data = model_data.intensity_data[0];
              if (intensity_data) {
                selectedPoint.value = intensity_data.values[pick_info.index];
              }
            }
          });
      }
    });
  }


  setBackgroundcolor(color: string): void {
    if (!this.viewer) { return; }
    this.viewer.setClearColor(parseInt('0x' + color.substring(1), 16));
  }

  resetView(): void {
    if (!this.viewer) { return; }
    this.opacity = 100;
    this.viewer.setView();
    this.setBackgroundcolor(this.bbrowserColor);
  };

  setRotation(axis: string): void {
    if (!this.viewer) { return; }
    this.viewer.autorotate[axis] = !this.viewer.autorotate[axis];
  };

  changeOpacity(alpha: number): void {
    alpha = Math.min(100, Math.max(0, alpha)) / 100.0;
    this.viewer.setTransparency(alpha,
      { shape_name: this.selectedShapeName });
  }

  // All code below is related to mni templates and nifti support.
  // It's untested and I cannot verify if it ever worked in the old implementation.
  // See above note about searhing git history for these supporting files.

  /*
  To understand the following code, this block could be useful:
  https://bl.ocks.org/rentzso/17af620dacaa1882f5e508664441df7b
  */
  addMniTemplate(): void {
    if (!this.viewer) { return; }
    this.http.get('workers/mni_152_template_t1.nii', { responseType: 'arraybuffer' })
      .subscribe((response: ArrayBuffer) => {
        var data = response,
          niftiHeader = null,
          niftiImage = null,
          niftiExt = null, axis, volume;

        if (nifti.isCompressed(data)) {
          data = nifti.decompress(data);
        }

        if (nifti.isNIFTI(data)) {
          niftiHeader = this.niftiHeader = nifti.readHeader(data);
          niftiImage = this.niftiImage = nifti.readImage(niftiHeader, data);

          if (nifti.hasExtension(niftiHeader)) {
            niftiExt = nifti.readExtensionData(niftiHeader, data);
          }
          volume = this.imageData(niftiImage, niftiHeader);
          axis = 0;
          this.showSlice(volume, Math.floor(volume.shapeX / 2), axis);
          axis = 1;
          this.showSlice(volume, Math.floor(volume.shapeY / 2), axis);
          axis = 2;
          this.showSlice(volume, Math.floor(volume.shapeZ / 2), axis);
        }
      });
  }

  imageData(niftiImage: ArrayBuffer, niftiHeader: nifti.NIFTI1 | nifti.NIFTI2) {
    var value, i, j, k;
    var view = new DataView(niftiImage);
    var size = niftiImage.byteLength / 2;
    var data = this.data = [] as number[];
    var max = view.getInt16(0, true);
    var min = view.getInt16(0, true);
    for (i = 1; i < size; i++) {
      value = view.getInt16(i * 2, true);
      min = Math.min(value, min);
      max = Math.max(value, max);
    }
    var shapeX = niftiHeader.dims[1];
    var shapeY = niftiHeader.dims[2];
    var shapeZ = niftiHeader.dims[3];
    var index = 0;
    for (k = 0; k < shapeZ; k++) {
      for (j = 0; j < shapeY; j++) {
        for (i = 0; i < shapeX; i++) {
          value = 255 * (view.getInt16(index++ * 2, true) - min) / (max - min);
          value = Math.round(value);
          value = Math.max(value, 0);
          value = Math.min(value, 255);
          data.push(value);
        }
      }
    }
    return {
      data: data,
      shapeX: shapeX,
      shapeY: shapeY,
      shapeZ: shapeZ,
    };
  }

  getSlice(data: Volume, index: number, axis: number) {
    var sliceRGBA, x, y, z, i, j;
    var shapeX = data.shapeX,
      shapeY = data.shapeY,
      shapeZ = data.shapeZ;
    var sliceZSize = shapeX * shapeY;
    if (axis === 0) {
      sliceRGBA = new Uint8Array(shapeY * shapeZ * 4);
      x = index;
      j = 0;
      for (z = 0; z < shapeZ; z++) {
        for (y = 0; y < shapeY; y++) {
          i = x + shapeX * y + sliceZSize * z;
          sliceRGBA[4 * j] = sliceRGBA[4 * j + 1] = sliceRGBA[4 * j + 2] = data.data[i];
          sliceRGBA[4 * j++ + 3] = 255;
        }
      }
    } else if (axis === 1) {
      sliceRGBA = new Uint8Array(shapeX * shapeZ * 4);
      y = index;
      j = 0;
      for (z = 0; z < shapeZ; z++) {
        for (x = 0; x < shapeX; x++) {
          i = x + shapeX * y + sliceZSize * z;
          sliceRGBA[4 * j] = sliceRGBA[4 * j + 1] = sliceRGBA[4 * j + 2] = data.data[i];
          sliceRGBA[4 * j++ + 3] = 255;
        }
      }
    } else if (axis === 2) {
      sliceRGBA = new Uint8Array(shapeX * shapeY * 4);
      z = index;
      j = 0;
      for (y = 0; y < shapeY; y++) {
        for (x = 0; x < shapeX; x++) {
          i = x + shapeX * y + sliceZSize * z;
          sliceRGBA[4 * j] = sliceRGBA[4 * j + 1] = sliceRGBA[4 * j + 2] = data.data[i];
          sliceRGBA[4 * j++ + 3] = 255;
        }
      }
    }
    return sliceRGBA;
  }

  showSlice(volume: Volume, index: number, axis: number) {
    var width, height;
    var THREE = BrainBrowser.SurfaceViewer.THREE;
    if (axis === 0) {
      width = volume.shapeY;
      height = volume.shapeZ;
    } else if (axis === 1) {
      width = volume.shapeX;
      height = volume.shapeZ;
    } else {
      width = volume.shapeX;
      height = volume.shapeY;
    }
    var sliceRGBA = this.getSlice(volume, index, axis);
    var texture = new THREE.DataTexture(sliceRGBA, width, height, THREE.RGBAFormat);
    // I am not sure why, but needsUpdate is needed for the texture definition.
    texture.needsUpdate = true;
    var geometry = new THREE.PlaneGeometry(width, height, 1, 1);

    var material = new THREE.MeshBasicMaterial(
      {
        map: texture,
        side: THREE.DoubleSide,
        opacity: 0.8,
        transparent: true,
      });
    var plane = new THREE.Mesh(geometry, material);
    this.viewer.model.add(plane);
    if (axis === 0) {
      plane.rotation.y -= Math.PI / 2;
      plane.rotation.x -= Math.PI / 2;
    } else if (axis === 1) {
      plane.rotation.x -= Math.PI / 2;
    } else {
      plane.rotation.x += Math.PI;
    }
    plane.position.x = Math.floor(volume.shapeX / 2) - 98;
    plane.position.y = Math.floor(volume.shapeY / 2) - 134;
    plane.position.z = Math.floor(volume.shapeZ / 2) - 72;
    this.viewer.render();
    this.setBackgroundcolor(this.bbrowserColor);
    this.hasMniTemplate = true;
    return {
      sliceRGBA: sliceRGBA,
      texture: texture,
    };
  }
}
